(() => {
  if (!customElements.get('zoria-products-carousel')) {
    class ZoriaProductsCarousel extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.track = this.querySelector('.z-products__grid--carousel');
        this.previous = this.querySelector('[name="previous"]');
        this.next = this.querySelector('[name="next"]');
        if (!this.track || !this.previous || !this.next) return;

        this.rtl = this.closest('[dir="rtl"]') !== null || document.documentElement.dir === 'rtl';
        this.previous.addEventListener('click', () => this.move(-1));
        this.next.addEventListener('click', () => this.move(1));
        this.handleScroll = () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.update());
        };
        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.handleScroll);
        cancelAnimationFrame(this.scrollFrame);
        this.resizeObserver?.disconnect();
      }

      move(direction) {
        const item = this.track.firstElementChild;
        if (!item) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const left = direction * (item.offsetWidth + gap) * (this.rtl ? -1 : 1);
        const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.track.scrollBy({ left, behavior });
      }

      update() {
        const position = this.rtl ? -this.track.scrollLeft : this.track.scrollLeft;
        const maximum = this.track.scrollWidth - this.track.clientWidth;
        this.previous.disabled = position <= 1;
        this.next.disabled = position >= maximum - 1;
        this.syncFocusableSlides();
      }

      syncFocusableSlides() {
        if (window.ZoriaCarouselFocus) {
          window.ZoriaCarouselFocus.sync(this.track);
          return;
        }
        const trackRect = this.track.getBoundingClientRect();
        [...this.track.children].forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          const offscreen = rect.right <= trackRect.left + 1 || rect.left >= trackRect.right - 1;
          slide.inert = offscreen;
          if (offscreen) {
            slide.dataset.zoriaOffscreen = 'true';
            slide.setAttribute('aria-hidden', 'true');
          } else if (slide.dataset.zoriaOffscreen === 'true') {
            delete slide.dataset.zoriaOffscreen;
            slide.removeAttribute('aria-hidden');
          }
        });
      }
    }

    customElements.define('zoria-products-carousel', ZoriaProductsCarousel);
  }

  if (!customElements.get('zoria-gift-widget')) {
    class ZoriaGiftWidget extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.dialog = this.querySelector('[id="ZoriaGiftFinderDialog"]');
        this.closeButton = this.querySelector('[data-gift-widget-close]');
        this.finder = this.querySelector('zoria-gift-finder');
        this.prompt = this.querySelector('.z-gift-widget__prompt');
        if (!this.dialog || !this.closeButton) return;

        this.promptTimer = setTimeout(() => {
          if (!this.prompt) return;
          if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
            this.prompt.hidden = true;
            return;
          }
          this.prompt.classList.add('is-hiding');
          this.promptHideTimer = setTimeout(() => { this.prompt.hidden = true; }, 480);
        }, 5000);

        this.openers = [...document.querySelectorAll('[data-gift-widget-open][aria-controls="ZoriaGiftFinderDialog"]')];
        this.handleOpen = (event) => this.openDialog(event.currentTarget);
        this.handleClose = () => this.closeDialog();
        this.handleCancel = (event) => {
          event.preventDefault();
          this.closeDialog();
        };
        this.handleBackdrop = (event) => {
          if (event.target !== this.dialog) return;
          const bounds = this.dialog.getBoundingClientRect();
          const outside = event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
          if (outside) this.closeDialog();
        };

        this.openers.forEach((opener) => opener.addEventListener('click', this.handleOpen));
        this.closeButton.addEventListener('click', this.handleClose);
        this.dialog.addEventListener('cancel', this.handleCancel);
        this.dialog.addEventListener('click', this.handleBackdrop);
      }

      disconnectedCallback() {
        clearTimeout(this.promptTimer);
        clearTimeout(this.promptHideTimer);
        this.openers?.forEach((opener) => opener.removeEventListener('click', this.handleOpen));
        this.closeButton?.removeEventListener('click', this.handleClose);
        this.dialog?.removeEventListener('cancel', this.handleCancel);
        this.dialog?.removeEventListener('click', this.handleBackdrop);
      }

      hydrateDialog() {
        const template = this.dialog?.querySelector('[data-gift-widget-template]');
        if (template instanceof HTMLTemplateElement) template.replaceWith(template.content.cloneNode(true));
        this.finder = this.querySelector('zoria-gift-finder');
      }

      openDialog(opener) {
        if (this.dialog.open) return;
        this.hydrateDialog();
        this.finder?.loadCatalog?.();
        this.lastOpener = opener;
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
        this.openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        this.closeButton.focus({ preventScroll: true });
      }

      closeDialog() {
        if (!this.dialog.open && !this.dialog.hasAttribute('open')) return;
        this.finder?.restart?.(false);
        if (typeof this.dialog.close === 'function') this.dialog.close();
        else this.dialog.removeAttribute('open');
        this.openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        this.lastOpener?.focus({ preventScroll: true });
      }
    }

    customElements.define('zoria-gift-widget', ZoriaGiftWidget);
  }

  if (customElements.get('zoria-gift-finder')) return;

  const TOKENS = {
    recipient: {
      self: ['self', 'myself', 'לעצמי'],
      woman: ['woman', 'women', 'female', 'אישה', 'נשים', 'בת זוג'],
      man: ['man', 'men', 'mens', 'male', 'גבר', 'בן זוג'],
      couple: ['couple', 'couples', 'pair', 'זוג', 'זוגי'],
      mother: ['mother', 'mom', 'mama', 'אמא', 'אמהות']
    },
    style: {
      delicate: ['delicate', 'dainty', 'fine', 'heart', 'עדין', 'עדינה', 'לב'],
      classic: ['classic', 'timeless', 'smooth', 'קלאסי', 'קלאסית'],
      bold: ['bold', 'statement', 'chunky', 'thick', 'נוכחות', 'עבה'],
      couples: ['couple', 'couples', 'pair', 'זוג', 'זוגי']
    },
    personalization: {
      engraving: ['engraving', 'engraved', 'dedication', 'personalization-name', 'personalization-names', 'initial', 'חריטה', 'הקדשה', 'אות', 'שם'],
      hidden_photo: ['hidden_photo', 'projection', 'תמונה נסתרת', 'מוצפנת'],
      visible_photo: ['visible_photo', 'disc photo', 'תמונה על התכשיט', 'תמונה אישית', 'דיסקית'],
      none: ['personalization none', 'ללא התאמה']
    }
  };

  const normalize = (value = '') => ` ${String(value).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `;
  const split = (value = '') => String(value).split(',').map((item) => item.trim()).filter(Boolean);
  const contains = (search, token) => search.includes(normalize(token));
  const money = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' });

  class ZoriaGiftFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.form = this.querySelector('[data-gift-finder-form]');
      this.steps = [...this.querySelectorAll('[data-gift-step]')];
      this.back = this.querySelector('[data-gift-back]');
      this.next = this.querySelector('[data-gift-next]');
      this.submit = this.querySelector('[data-gift-submit]');
      this.results = this.querySelector('[data-gift-results]');
      this.summary = this.querySelector('[data-gift-summary]');
      this.empty = this.querySelector('[data-gift-empty]');
      this.loading = this.querySelector('[data-gift-loading]');
      this.products = this.querySelector('[data-gift-products]');
      this.resetButton = this.querySelector('[data-gift-reset]');
      this.progress = this.querySelector('[data-gift-progress]');
      this.progressBar = this.querySelector('[data-gift-progress-bar]');
      this.index = 0;
      this.overrides = new Map();
      try {
        const data = JSON.parse(this.querySelector('[data-gift-overrides]')?.textContent || '[]');
        data.forEach((item) => item?.handle && this.overrides.set(item.handle, item));
      } catch (error) {
        console.error('ZORIA gift finder overrides could not be parsed.', error);
      }

      if (!this.form || this.steps.length === 0) return;
      this.classList.add('is-enhanced');
      this.form.addEventListener('change', (event) => {
        if (!this.started) {
          this.started = true;
          this.publish('zoria_gift_finder_started', 'started');
        }
        this.update();
        if (event.target.closest('[data-gift-step="recipient"]')) {
          const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
          this.next.scrollIntoView?.({ behavior, block: 'center' });
        }
      });
      this.next.addEventListener('click', () => this.showStep(this.index + 1));
      this.back.addEventListener('click', () => this.showStep(this.index - 1));
      this.form.addEventListener('submit', (event) => this.complete(event));
      this.resetButton?.addEventListener('click', () => this.restart());
      this.showStep(0, false);
      this.loadCatalog();
    }

    loadCatalog() {
      if (this.catalogPromise) return this.catalogPromise;
      if (!this.dataset.catalogUrl || !this.products) return Promise.resolve([]);
      if (this.loading) this.loading.hidden = false;

      this.catalogPromise = fetch(this.dataset.catalogUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Catalog request failed with ${response.status}`);
        return response.json();
      }).then(({ products = [] }) => {
        const fragment = document.createDocumentFragment();
        products.forEach((product) => {
          const card = this.createResult(product);
          if (card) fragment.appendChild(card);
        });
        this.products.replaceChildren(fragment);
        this.cards = [...this.products.querySelectorAll('.z-gift-result')];
        return this.cards;
      }).catch((error) => {
        console.error('ZORIA gift finder catalog could not be loaded.', error);
        if (this.loading) this.loading.textContent = 'לא הצלחנו לטעון את התכשיטים. נסו שוב בעוד רגע.';
        return [];
      }).finally(() => {
        if (this.loading && this.cards?.length) this.loading.hidden = true;
      });

      return this.catalogPromise;
    }

    createResult(product) {
      const variants = (product.variants || []).filter((variant) => variant.available);
      if (!product.handle || variants.length === 0) return null;
      const selected = variants.reduce((lowest, variant) => Number(variant.price) < Number(lowest.price) ? variant : lowest);
      const price = Math.round(Number(selected.price) * 100);
      if (!Number.isFinite(price)) return null;

      const override = this.overrides.get(product.handle) || {};
      const search = normalize([product.title, product.handle, product.product_type, ...(product.tags || [])].join(' '));
      let personalization = override.personalization || '';
      if (personalization === 'names') personalization = 'engraving';
      if (personalization === 'photo') {
        personalization = ['hidden', 'projection', 'נסתרת', 'מוצפנת'].some((token) => contains(search, token))
          ? 'hidden_photo'
          : 'visible_photo';
      }

      const result = document.createElement('div');
      result.className = 'z-gift-result';
      result.hidden = true;
      Object.assign(result.dataset, {
        recipient: override.recipient || '',
        style: override.style || '',
        personalization,
        search,
        price: String(price),
        available: 'true',
        handle: product.handle,
        collectionUrl: override.collectionUrl || this.dataset.allProductsUrl || '/collections/all'
      });

      const match = document.createElement('p');
      match.className = 'z-gift-result__match';
      match.dataset.giftMatch = '';
      result.append(match, this.createProductCard(product, selected));
      return result;
    }

    createProductCard(product, variant) {
      const url = `${this.dataset.productBaseUrl || '/products/'}${encodeURIComponent(product.handle)}`;
      const image = variant.featured_image || product.images?.[0];
      const wrapper = document.createElement('div');
      wrapper.className = 'product-card-wrapper underline-links-hover';
      const card = document.createElement('div');
      card.className = 'card card--standard card--media';
      const inner = document.createElement('div');
      inner.className = 'card__inner color-background-2 gradient ratio';
      inner.style.setProperty('--ratio-percent', '100%');
      const media = document.createElement('div');
      media.className = 'card__media';
      const mediaFrame = document.createElement('div');
      mediaFrame.className = 'media media--transparent';
      if (image?.src) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('aria-label', product.title);
        const img = document.createElement('img');
        img.src = image.src;
        img.alt = product.title;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = image.width || 600;
        img.height = image.height || 600;
        link.append(img);
        mediaFrame.append(link);
      }
      media.append(mediaFrame);
      inner.append(media);

      const content = document.createElement('div');
      content.className = 'card__content';
      const information = document.createElement('div');
      information.className = 'card__information';
      const heading = document.createElement('h4');
      heading.className = 'card__heading h5';
      const title = document.createElement('a');
      title.className = 'full-unstyled-link';
      title.href = url;
      title.textContent = product.title;
      heading.append(title);

      const price = document.createElement('div');
      price.className = 'price';
      const sale = document.createElement('span');
      sale.className = 'price-item price-item--sale price-item--last';
      sale.textContent = money.format(Number(variant.price));
      price.append(sale);
      if (Number(variant.compare_at_price) > Number(variant.price)) {
        const compare = document.createElement('s');
        compare.className = 'price-item price-item--regular';
        compare.textContent = money.format(Number(variant.compare_at_price));
        price.append(compare);
      }
      information.append(heading, price);
      content.append(information);
      card.append(inner, content);
      wrapper.append(card);
      return wrapper;
    }

    showStep(index, focus = true) {
      this.index = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== this.index; });
      this.back.hidden = this.index === 0;
      this.next.hidden = this.index === this.steps.length - 1;
      this.submit.hidden = this.index !== this.steps.length - 1;
      this.progress.textContent = `שלב ${this.index + 1} מתוך ${this.steps.length}`;
      this.progressBar.style.width = `${((this.index + 1) / this.steps.length) * 100}%`;
      this.update();
      if (focus) this.steps[this.index].querySelector('legend')?.focus();
    }

    update() {
      const selected = this.steps[this.index].querySelector('input:checked');
      this.next.disabled = !selected;
      this.submit.disabled = !selected;
    }

    profile(card, key) {
      const raw = card.dataset[key] || '';
      if (raw.trim()) {
        const explicit = split(raw);
        return { values: new Set(explicit.filter((value) => value !== 'any')), universal: explicit.includes('any'), explicit: true };
      }

      const search = normalize(card.dataset.search);
      const inferred = Object.entries(TOKENS[key]).filter(([, tokens]) => tokens.some((token) => contains(search, token))).map(([value]) => value);
      if (key === 'personalization' && inferred.length === 0) inferred.push('none');
      return { values: new Set(inferred), universal: false, explicit: false };
    }

    inBudget(price, budget) {
      if (budget === 'any') return true;
      if (!Number.isFinite(price)) return false;
      if (budget === 'under-250') return price <= 25000;
      if (budget === '250-350') return price >= 25000 && price <= 35000;
      if (budget === '350-499') return price >= 35000 && price <= 49900;
      return true;
    }

    rank(cards, answers) {
      const seen = new Set();
      return cards.map((card, index) => {
        const handle = card.dataset.handle || `product-${index}`;
        if (card.dataset.available === 'false' || seen.has(handle)) return null;
        seen.add(handle);

        const price = Number(card.dataset.price);
        if (!this.inBudget(price, answers.budget)) return null;

        const recipient = this.profile(card, 'recipient');
        const style = this.profile(card, 'style');
        const personalization = this.profile(card, 'personalization');
        const matches = [];
        let score = answers.budget === 'any' ? 0 : 4;
        if (answers.budget !== 'any') matches.push('budget');

        if (answers.recipient === 'self') {
          if (recipient.values.has('self')) { score += 6; matches.push('recipient'); }
        } else if (recipient.values.has(answers.recipient)) {
          score += 6;
          matches.push('recipient');
        } else if (recipient.explicit && !recipient.universal) {
          return null;
        } else if (recipient.universal) {
          score += 1;
        }

        if (personalization.values.has(answers.personalization)) {
          score += 8;
          matches.push('personalization');
        } else if (personalization.universal) {
          score += 2;
        } else {
          return null;
        }

        if (style.values.has(answers.style)) {
          score += 3;
          matches.push('style');
        } else if (style.universal) {
          score += 0.5;
        }

        return { card, index, score, matches };
      }).filter(Boolean).sort((a, b) => b.score - a.score || a.index - b.index);
    }

    async complete(event) {
      event.preventDefault();
      if (!this.form.reportValidity()) return;

      const answers = Object.fromEntries(new FormData(this.form));
      this.submit.disabled = true;
      const cards = await this.loadCatalog();
      this.submit.disabled = false;
      const ranked = this.rank(cards, answers);
      const visible = ranked.slice(0, 4);

      cards.forEach((card) => { card.hidden = !visible.some((result) => result.card === card); });
      visible.forEach(({ card, matches }) => {
        const match = card.querySelector?.('[data-gift-match]');
        if (match) match.textContent = this.matchReason(matches);
      });

      const selectedLabels = [...(this.form.querySelectorAll?.('input:checked') || [])]
        .map((input) => input.nextElementSibling?.textContent.trim()).filter(Boolean);
      if (this.summary) {
        this.summary.textContent = visible.length
          ? `${visible.length} מוצרים זמינים שנבחרו לפי ${selectedLabels.join(' · ')}`
          : 'לא מצאנו התאמה מלאה לבחירות שלכם. נסו לשנות תשובה אחת או עברו לכל התכשיטים.';
      }
      if (this.empty) this.empty.hidden = visible.length > 0;

      const collectionLink = this.querySelector('[data-gift-collection-link]');
      if (visible[0]?.card.dataset.collectionUrl && collectionLink) collectionLink.href = visible[0].card.dataset.collectionUrl;
      this.form.hidden = true;
      this.results.hidden = false;
      this.results.focus();
      this.publish('zoria_gift_finder_completed', visible.length ? 'matched' : 'no_match');
    }

    matchReason(matches) {
      if (matches.includes('personalization') && matches.includes('budget')) return 'מתאים להתאמה ולתקציב שבחרתם';
      if (matches.includes('personalization')) return 'מתאים לסוג ההתאמה שבחרתם';
      if (matches.includes('recipient') && matches.includes('budget')) return 'מתאים למקבל ולתקציב שבחרתם';
      return 'תכשיט שמתאים למה שבחרתם';
    }

    restart(focus = true) {
      this.form.reset();
      this.form.hidden = false;
      this.results.hidden = true;
      this.started = false;
      this.showStep(0, focus);
      if (focus) this.form.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }

    publish(name, status) {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish !== 'function') return;
      try {
        Promise.resolve(publish.call(window.Shopify.analytics, name, { source: 'homepage', status })).catch(() => {});
      } catch {}
    }
  }

  customElements.define('zoria-gift-finder', ZoriaGiftFinder);
})();
